import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Elements } from "@stripe/react-stripe-js";

import MobileTopBar from "mobile/components/MobileTopBar";

const C = {
  canvas: "#F7F8FA",
  surface: "#FFFFFF",
  ink: "#0F0F12",
  muted: "#55575C",
  subtle: "#6E7278",
  hairline: "rgba(15,15,18,0.1)",
  accent: "#FF5C00",
  accentHover: "#E04F00",
  accentSoft: "rgba(255,92,0,0.12)",
  green: "#147A5C",
  greenSoft: "rgba(20,122,92,0.12)",
  red: "#A51812",
  redSoft: "rgba(215,38,30,0.07)",
  warn: "#8F3700",
  warnSoft: "rgba(255,106,0,0.1)",
  fontMono: 'ui-monospace, "SF Mono", "Cascadia Code", monospace',
};

const STEP_META = {
  cart: { icon: "shopping_bag", label: "Cart" },
  shipping: { icon: "location_on", label: "Ship" },
  delivery: { icon: "local_shipping", label: "Rate" },
  payment: { icon: "credit_card", label: "Pay" },
  review: { icon: "receipt_long", label: "Review" },
};

// Above MobileShell bottom nav — same offset as cart MxShell/VbsShell footers.
const stickyFooterSx = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: "calc(70px + env(safe-area-inset-bottom))",
  zIndex: 1400,
  px: 2,
  py: 1.25,
  bgcolor: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(16px)",
  borderTop: `1px solid ${C.hairline}`,
  boxShadow: "0 -10px 28px rgba(15,15,18,0.06)",
};

const cardSx = {
  p: 1.5,
  borderRadius: "14px",
  bgcolor: C.surface,
  border: `1px solid ${C.hairline}`,
  boxShadow: "0 8px 22px rgba(15,15,18,0.04)",
};

const softCardSx = {
  p: 2,
  borderRadius: "14px",
  bgcolor: C.surface,
  border: `1px solid ${C.hairline}`,
};

function formatMoneyAmount(value, currency) {
  const n = Number(value || 0);
  const amount = Number.isFinite(n) ? n : 0;
  try {
    return new Intl.NumberFormat(currency === "MXN" ? "es-MX" : "en-US", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11.5,
        fontWeight: 800,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: C.subtle,
        mb: 1,
        px: 0.15,
      }}
    >
      {children}
    </Typography>
  );
}

SectionLabel.propTypes = { children: PropTypes.node.isRequired };

function Notice({ tone = "info", children, action }) {
  const tones = {
    error: { bg: C.redSoft, color: C.red, icon: "error_outline" },
    warning: { bg: C.warnSoft, color: C.warn, icon: "warning_amber" },
    success: { bg: C.greenSoft, color: C.green, icon: "check_circle" },
    info: { bg: "rgba(15,15,18,0.04)", color: C.muted, icon: "info" },
  };
  const t = tones[tone] || tones.info;
  return (
    <Box
      sx={{
        p: 1.35,
        borderRadius: "14px",
        bgcolor: t.bg,
        border: `1px solid ${C.hairline}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Icon sx={{ fontSize: 20, color: t.color, mt: 0.1 }}>{t.icon}</Icon>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: C.ink, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
            {children}
          </Typography>
          {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
        </Box>
      </Stack>
    </Box>
  );
}

Notice.propTypes = {
  tone: PropTypes.oneOf(["info", "warning", "error", "success"]),
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
};

Notice.defaultProps = {
  tone: "info",
  action: null,
};

function SoftField({ label, value, onChange, disabled }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 750, color: C.muted, mb: 0.55, px: 0.15 }}>
        {label}
      </Typography>
      <Box
        component="input"
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          border: `1px solid ${C.hairline}`,
          borderRadius: "10px",
          bgcolor: C.canvas,
          color: C.ink,
          fontSize: 15,
          fontWeight: 650,
          px: 1.25,
          py: 1.2,
          outline: "none",
          fontFamily: "inherit",
          opacity: disabled ? 0.55 : 1,
          "&:focus": {
            borderColor: `${C.accent}99`,
            bgcolor: C.surface,
            boxShadow: `0 0 0 3px ${C.accentSoft}`,
          },
        }}
      />
    </Box>
  );
}

SoftField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SoftField.defaultProps = {
  value: "",
  disabled: false,
};

function SegmentedControl({ value, onChange, options }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        gap: 0.4,
        p: 0.4,
        borderRadius: "12px",
        bgcolor: "rgba(15,15,18,0.05)",
        border: `1px solid ${C.hairline}`,
      }}
    >
      {options.map((opt) => {
        const selected = value === opt.key;
        return (
          <ButtonBase
            key={opt.key}
            onClick={() => onChange(opt.key)}
            sx={{
              py: 1,
              px: 0.75,
              borderRadius: "10px",
              bgcolor: selected ? C.surface : "transparent",
              boxShadow: selected ? "0 4px 12px rgba(15,15,18,0.08)" : "none",
              color: selected ? C.ink : C.muted,
            }}
          >
            <Stack direction="row" spacing={0.55} alignItems="center" justifyContent="center">
              {opt.icon ? (
                <Icon sx={{ fontSize: 18, color: selected ? C.accent : C.subtle }}>{opt.icon}</Icon>
              ) : null}
              <Typography sx={{ fontSize: 13.5, fontWeight: 800 }}>{opt.label}</Typography>
            </Stack>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

SegmentedControl.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }),
  ).isRequired,
};

function SelectableCard({ selected, onClick, icon, title, meta, trailing, disabled = false }) {
  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        borderRadius: "14px",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Box
        sx={{
          ...cardSx,
          borderColor: selected ? `${C.accent}66` : C.hairline,
          bgcolor: selected ? "rgba(255,92,0,0.04)" : C.surface,
          boxShadow: selected
            ? `0 8px 22px rgba(255,92,0,0.1), inset 0 0 0 1px ${C.accent}22`
            : cardSx.boxShadow,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              bgcolor: selected ? C.accentSoft : "rgba(15,15,18,0.05)",
              color: selected ? C.accent : C.muted,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22 }}>{icon}</Icon>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 15.25, fontWeight: 750, color: C.ink, lineHeight: 1.25 }}>
              {title}
            </Typography>
            {meta ? (
              <Typography sx={{ mt: 0.35, fontSize: 13, color: C.muted, lineHeight: 1.4 }}>
                {meta}
              </Typography>
            ) : null}
          </Box>
          {trailing != null ? (
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: C.ink,
                flexShrink: 0,
                letterSpacing: -0.2,
              }}
            >
              {trailing}
            </Typography>
          ) : null}
          <Icon
            sx={{
              fontSize: 22,
              color: selected ? C.accent : C.subtle,
              flexShrink: 0,
              mt: 0.15,
            }}
          >
            {selected ? "check_circle" : "radio_button_unchecked"}
          </Icon>
        </Stack>
      </Box>
    </ButtonBase>
  );
}

SelectableCard.propTypes = {
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  trailing: PropTypes.node,
  disabled: PropTypes.bool,
};

SelectableCard.defaultProps = {
  selected: false,
  meta: null,
  trailing: null,
  disabled: false,
};

function StepDots({ steps, currentStep, currentStepIndex, onSelect }) {
  return (
    <Box sx={{ px: 0.25, mb: 1.75, mt: 0.35 }}>
      <Stack direction="row" alignItems="center" justifyContent="center">
        {steps.map((step, index) => {
          const meta = STEP_META[step] || { icon: "circle", label: step };
          const active = step === currentStep;
          const done = index < currentStepIndex;
          const canJump = index <= currentStepIndex + 1;
          return (
            <Stack key={step} direction="row" alignItems="center" sx={{ flex: index < steps.length - 1 ? 1 : "0 0 auto" }}>
              <ButtonBase
                disabled={!canJump}
                onClick={() => onSelect(step)}
                aria-label={meta.label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.35,
                  opacity: canJump ? 1 : 0.4,
                  borderRadius: "12px",
                  px: 0.35,
                  py: 0.25,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "11px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: active || done ? C.accentSoft : "rgba(15,15,18,0.05)",
                    color: active || done ? C.accent : C.subtle,
                    boxShadow: active ? `0 0 0 2px ${C.accent}44` : "none",
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>{done && !active ? "check" : meta.icon}</Icon>
                </Box>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: active ? C.accent : C.muted,
                    letterSpacing: 0.15,
                    lineHeight: 1,
                  }}
                >
                  {meta.label}
                </Typography>
              </ButtonBase>
              {index < steps.length - 1 ? (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    mx: 0.35,
                    mb: 1.6,
                    borderRadius: 99,
                    bgcolor: index < currentStepIndex ? `${C.accent}55` : "rgba(15,15,18,0.08)",
                    minWidth: 12,
                  }}
                />
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

StepDots.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentStep: PropTypes.string.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function SummaryRow({ label, value, strong = false, accent = false }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
      <Typography
        sx={{
          fontSize: strong ? 15 : 14.5,
          color: strong ? C.ink : C.muted,
          fontWeight: strong ? 750 : 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? 18 : 14.5,
          fontWeight: strong ? 800 : 750,
          color: accent ? C.accent : C.ink,
          letterSpacing: strong ? -0.25 : 0,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  strong: PropTypes.bool,
  accent: PropTypes.bool,
};

SummaryRow.defaultProps = {
  strong: false,
  accent: false,
};

function LineItemCard({ line, currency, displayMoney }) {
  const qty = Number(line.qty || 1) || 1;
  const unit = Number(line.unitPrice || 0) || 0;
  const core = Number(line.coreCharge || 0) || 0;
  const lineTotal = qty * (unit + core);

  return (
    <Box sx={cardSx}>
      <Stack direction="row" spacing={1.35} alignItems="flex-start">
        {line.imageUrl ? (
          <Box
            component="img"
            src={line.imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              width: 72,
              height: 72,
              objectFit: "cover",
              borderRadius: "12px",
              bgcolor: C.canvas,
              flexShrink: 0,
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "12px",
              bgcolor: C.canvas,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              border: `1px solid ${C.hairline}`,
            }}
          >
            <Icon sx={{ color: C.subtle, fontSize: 26 }}>inventory_2</Icon>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {line.sku ? (
            <Typography
              sx={{
                fontFamily: C.fontMono,
                fontSize: 10.5,
                fontWeight: 750,
                color: C.accent,
                letterSpacing: 0.7,
                textTransform: "uppercase",
              }}
            >
              {line.sku}
            </Typography>
          ) : null}
          <Typography
            sx={{
              mt: 0.2,
              fontSize: 15,
              fontWeight: 750,
              lineHeight: 1.28,
              color: C.ink,
            }}
          >
            {line.name || line.sku || "Item"}
          </Typography>
          <Typography sx={{ mt: 0.4, fontSize: 13, color: C.muted, fontWeight: 600 }}>
            Qty {qty} · {displayMoney(unit, currency)}
            {core > 0 ? ` + ${displayMoney(core, currency)} core` : ""}
          </Typography>
          <Typography
            sx={{
              mt: 0.65,
              fontSize: 16,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: -0.2,
              textAlign: "right",
            }}
          >
            {displayMoney(lineTotal, currency)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

LineItemCard.propTypes = {
  line: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired,
  displayMoney: PropTypes.func.isRequired,
};

export default function MobileCheckoutFlow({
  isMx,
  currency: currencyProp,
  formatMoney: formatMoneyProp,
  stepTitle,
  checkoutSteps,
  currentStep,
  currentStepIndex,
  setCurrentStep,
  usesDistributorRequestFlow,
  linkedDistributorName,
  meLoading,
  meError,
  intentError,
  shippingError,
  postCheckoutMessage,
  bankTransferInstructions,
  lines,
  subtotal,
  shippingAddressMode,
  setShippingAddressMode,
  shippingAddresses,
  selectedSavedAddress,
  setSelectedShippingAddressId,
  toAddress,
  setToAddress,
  useCurrentLocation,
  locationMessage,
  wantsFacturaToRfc,
  setWantsFacturaToRfc,
  satMissing,
  canCheckoutMx,
  onNavigateProfile,
  activeShipToSummary,
  onQuote,
  canQuote,
  shippingLoading,
  rates,
  selectedRateId,
  setSelectedRateId,
  formatRateLabel,
  isDistributor,
  distributorHasCredit,
  bankTransferAvailable,
  plaidTransferAvailable,
  checkoutProfile,
  paymentCollectionMethod,
  setSelectedPaymentCollectionMethod,
  stripePublishableKey,
  selectedRate,
  pricing,
  shippingAmount,
  processingFeeAmount,
  processingFeeConfig,
  orderTotal,
  clientSecret,
  stripePromise,
  orderNumber,
  StripePayment,
  onStripePaid,
  plaidSalesOrderDocumentId,
  onBack,
  primaryAction,
  primaryDisabled,
  primaryLabel,
}) {
  const currency = currencyProp || (isMx ? "MXN" : "USD");
  const displayMoney =
    formatMoneyProp || ((value) => formatMoneyAmount(value, currency));
  const title = stepTitle[currentStep] || "Checkout";
  const showFooterTotal = currentStep === "review" || currentStep === "cart";
  const footerAmount =
    currentStep === "review" ? displayMoney(orderTotal, currency) : displayMoney(subtotal, currency);

  const addressModeOptions = [
    ...(shippingAddresses.length
      ? [{ key: "saved", label: isMx ? "Guardada" : "Saved", icon: "bookmark" }]
      : []),
    {
      key: "manual",
      label: isMx ? "Nueva" : "New",
      icon: "edit_location_alt",
    },
  ];

  const paymentOptions = [];
  if (!(isDistributor && distributorHasCredit)) {
    paymentOptions.push({
      key: "stripe_card",
      label: isMx ? "Tarjeta" : "Card",
      icon: "credit_card",
      meta: isMx
        ? "Pago seguro con tarjeta"
        : "Secure card payment via Stripe",
    });
    if (!isMx && plaidTransferAvailable) {
      paymentOptions.push({
        key: "plaid_transfer",
        label: "Bank",
        icon: "account_balance",
        meta: "Pay from your bank account via Plaid",
      });
    }
    if (bankTransferAvailable) {
      paymentOptions.push({
        key: "bank_transfer",
        label: isMx ? "Transferencia" : "Transfer",
        icon: "payments",
        meta: isMx
          ? "Instrucciones por correo; confirmamos el pago antes de surtir"
          : "Bank instructions emailed; we confirm payment before fulfillment",
      });
    }
  }

  const addressFields = [
    ["name", isMx ? "Nombre" : "Name"],
    ["street1", isMx ? "Calle y número" : "Street"],
    ["street2", isMx ? "Interior / Suite (opcional)" : "Apt / Suite (optional)"],
    ["city", isMx ? "Ciudad" : "City"],
    ["state", isMx ? "Estado" : "State"],
    ["zip", isMx ? "Código postal" : "ZIP"],
    ["country", isMx ? "País" : "Country"],
    ["phone", isMx ? "Teléfono" : "Phone"],
  ];

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: C.canvas,
        color: C.ink,
        pb: "calc(7.5rem + 70px + env(safe-area-inset-bottom))",
      }}
    >
      <Box sx={{ px: 2, pt: 0.35 }}>
        <MobileTopBar
          title={title}
          subtitle={
            usesDistributorRequestFlow
              ? linkedDistributorName || "Distributor request"
              : `${currentStepIndex + 1} / ${checkoutSteps.length}`
          }
          onBack={onBack}
        />

        <StepDots
          steps={checkoutSteps}
          currentStep={currentStep}
          currentStepIndex={currentStepIndex}
          onSelect={setCurrentStep}
        />

        <Stack spacing={1.35}>
          {meError ? <Notice tone="error">{meError}</Notice> : null}
          {meLoading ? (
            <Box sx={{ ...softCardSx, display: "flex", alignItems: "center", gap: 1.1, py: 1.5 }}>
              <CircularProgress size={18} sx={{ color: C.accent }} />
              <Typography sx={{ fontSize: 14, color: C.muted, fontWeight: 700 }}>
                Loading profile…
              </Typography>
            </Box>
          ) : null}
          {intentError ? <Notice tone="error">{intentError}</Notice> : null}
          {shippingError ? <Notice tone="error">{shippingError}</Notice> : null}
          {postCheckoutMessage ? (
            <Notice tone={postCheckoutMessage.type === "success" ? "success" : postCheckoutMessage.type || "info"}>
              {postCheckoutMessage.text}
            </Notice>
          ) : null}
          {bankTransferInstructions ? (
            <Notice tone="info">{bankTransferInstructions}</Notice>
          ) : null}

          {currentStep === "cart" ? (
            <Stack spacing={1.35}>
              {(lines || []).map((line) => (
                <LineItemCard key={line.documentId} line={line} currency={currency} displayMoney={displayMoney} />
              ))}
              <Box sx={softCardSx}>
                <SectionLabel>{isMx ? "Resumen" : "Summary"}</SectionLabel>
                <SummaryRow
                  label={isMx ? "Total del carrito" : "Cart total"}
                  value={displayMoney(subtotal, currency)}
                  strong
                  accent
                />
              </Box>
            </Stack>
          ) : null}

          {currentStep === "shipping" ? (
            <Stack spacing={1.35}>
              {addressModeOptions.length > 1 ? (
                <SegmentedControl
                  value={shippingAddressMode}
                  onChange={(key) => {
                    if (key === "saved" && !shippingAddresses.length) return;
                    setShippingAddressMode(key);
                  }}
                  options={addressModeOptions}
                />
              ) : null}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <SectionLabel>{isMx ? "Dirección" : "Address"}</SectionLabel>
                <ButtonBase
                  onClick={useCurrentLocation}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.65,
                    mb: 1,
                    borderRadius: "10px",
                    bgcolor: C.accentSoft,
                    color: C.accent,
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>my_location</Icon>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                    {isMx ? "Ubicación" : "Locate"}
                  </Typography>
                </ButtonBase>
              </Stack>

              {locationMessage ? (
                <Notice tone={locationMessage.type === "error" ? "error" : "info"}>
                  {locationMessage.text}
                </Notice>
              ) : null}

              {shippingAddressMode === "saved" ? (
                <Stack spacing={1}>
                  {shippingAddresses.length ? (
                    shippingAddresses.map((address) => (
                      <SelectableCard
                        key={address.addressId}
                        selected={
                          String(selectedSavedAddress?.addressId) === String(address.addressId)
                        }
                        onClick={() => setSelectedShippingAddressId(address.addressId)}
                        icon={address.isPrimary ? "home" : "place"}
                        title={`${address.label}${address.isPrimary ? " · Primary" : ""}`}
                        meta={[
                          address.street || address.street1,
                          `${address.city || ""}, ${address.state || ""} ${
                            address.postalCode || address.zip || ""
                          }`.trim(),
                          address.country,
                        ]
                          .filter((part) => String(part || "").trim())
                          .join(" · ")}
                      />
                    ))
                  ) : (
                    <Notice tone="warning">
                      No saved addresses yet. Enter an address or add one in Profile.
                    </Notice>
                  )}
                </Stack>
              ) : (
                <Box sx={{ ...softCardSx, p: 1.5 }}>
                  <Stack spacing={1.2}>
                    {addressFields.map(([key, label]) => (
                      <SoftField
                        key={key}
                        label={label}
                        value={toAddress[key] || ""}
                        disabled={!canCheckoutMx}
                        onChange={(next) =>
                          setToAddress((prev) => ({ ...prev, [key]: next }))
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {isMx ? (
                <Box sx={softCardSx}>
                  <ButtonBase
                    onClick={() => setWantsFacturaToRfc(!wantsFacturaToRfc)}
                    sx={{
                      display: "flex",
                      width: "100%",
                      textAlign: "left",
                      borderRadius: "12px",
                      alignItems: "center",
                      gap: 1.15,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: wantsFacturaToRfc ? C.accentSoft : "rgba(15,15,18,0.05)",
                        color: wantsFacturaToRfc ? C.accent : C.muted,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }}>receipt</Icon>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 750, color: C.ink }}>
                        Factura a mi RFC
                      </Typography>
                      <Typography sx={{ mt: 0.25, fontSize: 12.75, color: C.muted, lineHeight: 1.35 }}>
                        CFDI con los datos fiscales de su perfil
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 44,
                        height: 26,
                        borderRadius: 99,
                        bgcolor: wantsFacturaToRfc ? C.accent : "rgba(15,15,18,0.14)",
                        position: "relative",
                        flexShrink: 0,
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 3,
                          left: wantsFacturaToRfc ? 20 : 3,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: "#fff",
                          boxShadow: "0 1px 4px rgba(15,15,18,0.2)",
                          transition: "left 0.15s ease",
                        }}
                      />
                    </Box>
                  </ButtonBase>

                  {wantsFacturaToRfc && satMissing.length ? (
                    <Box sx={{ mt: 1.25 }}>
                      <Notice
                        tone="warning"
                        action={
                          <ButtonBase
                            onClick={onNavigateProfile}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 1.1,
                              py: 0.7,
                              borderRadius: "10px",
                              bgcolor: C.surface,
                              border: `1px solid ${C.hairline}`,
                              color: C.ink,
                            }}
                          >
                            <Icon sx={{ fontSize: 18, color: C.accent }}>person</Icon>
                            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Completar perfil</Typography>
                          </ButtonBase>
                        }
                      >
                        SAT fields required: {satMissing.join(", ")}.
                      </Notice>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          ) : null}

          {currentStep === "delivery" ? (
            <Stack spacing={1.35}>
              <Box sx={softCardSx}>
                <Stack direction="row" spacing={1.15} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      bgcolor: C.accentSoft,
                      color: C.accent,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }}>place</Icon>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: C.subtle, letterSpacing: 0.8, textTransform: "uppercase" }}>
                      Ship to
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontSize: 14.5, fontWeight: 700, lineHeight: 1.4, color: C.ink }}>
                      {activeShipToSummary}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <ButtonBase
                onClick={onQuote}
                disabled={!canQuote || shippingLoading}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  borderRadius: "14px",
                  opacity: !canQuote || shippingLoading ? 0.55 : 1,
                }}
              >
                <Box sx={cardSx}>
                  <Stack direction="row" spacing={1.15} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: C.accentSoft,
                        color: C.accent,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {shippingLoading ? (
                        <CircularProgress size={18} sx={{ color: C.accent }} />
                      ) : (
                        <Icon sx={{ fontSize: 22 }}>local_shipping</Icon>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15.25, fontWeight: 750, color: C.ink }}>
                        {shippingLoading ? "Quoting rates…" : "Get shipping rates"}
                      </Typography>
                      <Typography sx={{ mt: 0.25, fontSize: 13, color: C.muted, fontWeight: 600 }}>
                        Carrier options for this address
                      </Typography>
                    </Box>
                    <Icon sx={{ color: C.subtle, fontSize: 22 }}>chevron_right</Icon>
                  </Stack>
                </Box>
              </ButtonBase>

              {rates.length ? (
                <Box>
                  <SectionLabel>{isMx ? "Tarifas" : "Rates"}</SectionLabel>
                  <Stack spacing={1}>
                    {rates.map((rate) => {
                      const amount =
                        rate?.totalWithProcessingFee != null
                          ? Number(rate.totalWithProcessingFee)
                          : Number(rate?.rate || 0);
                      return (
                        <SelectableCard
                          key={String(rate.id)}
                          selected={String(selectedRateId) === String(rate.id)}
                          onClick={() => setSelectedRateId(rate.id)}
                          icon="local_shipping"
                          title={`${rate?.carrier || ""} ${rate?.service || ""}`.trim() || "Rate"}
                          meta={formatRateLabel(rate)}
                          trailing={displayMoney(amount, currency)}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          ) : null}

          {currentStep === "payment" ? (
            <Stack spacing={1.35}>
              {isDistributor && distributorHasCredit ? (
                <Box sx={cardSx}>
                  <Stack direction="row" spacing={1.15} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: C.greenSoft,
                        color: C.green,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }}>account_balance_wallet</Icon>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 15.5, fontWeight: 750, color: C.ink }}>
                        On account
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 13.25, color: C.muted, lineHeight: 1.4 }}>
                        {checkoutProfile.paymentTerms || "Configured credit terms"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <SectionLabel>{isMx ? "Método de pago" : "Payment method"}</SectionLabel>
                  <Stack spacing={1}>
                    {paymentOptions.map((opt) => (
                      <SelectableCard
                        key={opt.key}
                        selected={paymentCollectionMethod === opt.key}
                        onClick={() => setSelectedPaymentCollectionMethod(opt.key)}
                        icon={opt.icon}
                        title={opt.label}
                        meta={opt.meta}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {!stripePublishableKey && paymentCollectionMethod === "stripe_card" ? (
                <Notice tone="warning">
                  Stripe publishable key is not configured for this frontend build.
                </Notice>
              ) : null}
              {paymentCollectionMethod === "plaid_transfer" ? (
                <Notice tone="info">
                  Pay by bank opens Plaid after review. The order stays unpaid until funds settle.
                </Notice>
              ) : null}
              {paymentCollectionMethod === "bank_transfer" ? (
                <Notice tone="info">
                  {isMx
                    ? "Tu pedido se crea de inmediato. Te enviamos instrucciones de transferencia y confirmamos el pago antes de surtir."
                    : "Your order is placed now. Bank transfer instructions are emailed and fulfillment begins after payment confirmation."}
                </Notice>
              ) : null}
              {paymentCollectionMethod === "stripe_card" ? (
                <Notice tone="info">
                  Card payment is collected securely through Stripe after review.
                </Notice>
              ) : null}
            </Stack>
          ) : null}

          {currentStep === "review" ? (
            <Stack spacing={1.35}>
              <Box sx={softCardSx}>
                <Stack direction="row" spacing={1.15} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      bgcolor: C.accentSoft,
                      color: C.accent,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }}>place</Icon>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 800,
                        color: C.subtle,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                      }}
                    >
                      Ship to
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontSize: 14.5, fontWeight: 700, lineHeight: 1.4 }}>
                      {activeShipToSummary}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {!usesDistributorRequestFlow && selectedRate ? (
                <Box sx={softCardSx}>
                  <Stack direction="row" spacing={1.15} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: C.accentSoft,
                        color: C.accent,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }}>local_shipping</Icon>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          fontWeight: 800,
                          color: C.subtle,
                          letterSpacing: 0.8,
                          textTransform: "uppercase",
                        }}
                      >
                        Delivery
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 14.5, fontWeight: 700, lineHeight: 1.4 }}>
                        {formatRateLabel(selectedRate)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ) : null}

              <Box sx={softCardSx}>
                <SectionLabel>{isMx ? "Resumen" : "Summary"}</SectionLabel>
                <Stack spacing={0.9}>
                  <SummaryRow
                    label="Items"
                    value={displayMoney(pricing?.itemsSubtotal ?? subtotal, currency)}
                  />
                  <SummaryRow
                    label="Shipping"
                    value={displayMoney(pricing?.shippingAmount ?? shippingAmount, currency)}
                  />
                  {Number(processingFeeAmount || pricing?.processingFeeAmount || 0) > 0 ? (
                    <SummaryRow
                      label={
                        pricing?.processingFeeLabel ||
                        processingFeeConfig?.label ||
                        "Processing fee"
                      }
                      value={displayMoney(
                        pricing?.processingFeeAmount ?? processingFeeAmount,
                        currency,
                      )}
                    />
                  ) : null}
                  <Box sx={{ height: 1, bgcolor: C.hairline, my: 0.35 }} />
                  <SummaryRow
                    label={isMx ? "Total" : "Total"}
                    value={displayMoney(orderTotal, currency)}
                    strong
                    accent
                  />
                </Stack>
              </Box>

              <Stack spacing={1}>
                {(lines || []).map((line) => (
                  <LineItemCard key={`review-${line.documentId}`} line={line} currency={currency} displayMoney={displayMoney} />
                ))}
              </Stack>

              {clientSecret && stripePromise ? (
                <Box sx={{ ...softCardSx, p: 1.5 }}>
                  <SectionLabel>Card payment</SectionLabel>
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <StripePayment
                      clientSecret={clientSecret}
                      orderNumber={orderNumber || "WEB"}
                      onPaid={onStripePaid}
                    />
                  </Elements>
                </Box>
              ) : null}

              {paymentCollectionMethod === "plaid_transfer" && plaidSalesOrderDocumentId ? (
                <Notice tone="info">
                  Plaid authorization is open. If the bank window was closed, use the review action to
                  restart checkout for a fresh authorization.
                </Notice>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Box>

      <Box sx={stickyFooterSx}>
        <Stack spacing={1.05}>
          {showFooterTotal ? (
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
                {currentStep === "review"
                  ? isMx
                    ? "Total a pagar"
                    : "Order total"
                  : isMx
                    ? "Total del carrito"
                    : "Cart total"}
              </Typography>
              <Typography
                sx={{ fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: -0.3 }}
              >
                {footerAmount}
              </Typography>
            </Stack>
          ) : null}
          <ButtonBase
            type="button"
            onClick={primaryAction}
            disabled={primaryDisabled}
            sx={{
              width: "100%",
              bgcolor: primaryDisabled ? "rgba(15,15,18,0.18)" : C.accent,
              color: "#fff",
              py: 1.45,
              px: 2,
              borderRadius: "10px",
              fontSize: 15,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              boxShadow: primaryDisabled ? "none" : "0 10px 24px rgba(255,92,0,0.28)",
              "&:hover": {
                bgcolor: primaryDisabled ? "rgba(15,15,18,0.18)" : C.accentHover,
              },
            }}
          >
            {currentStep === "review" ? <Icon sx={{ fontSize: 20 }}>lock</Icon> : null}
            <span>{primaryLabel}</span>
            <Icon sx={{ fontSize: 20 }}>
              {currentStep === "review" ? "shopping_bag" : "arrow_forward"}
            </Icon>
          </ButtonBase>
        </Stack>
      </Box>
    </Box>
  );
}

MobileCheckoutFlow.propTypes = {
  isMx: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  formatMoney: PropTypes.func,
  stepTitle: PropTypes.object.isRequired,
  checkoutSteps: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentStep: PropTypes.string.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  usesDistributorRequestFlow: PropTypes.bool.isRequired,
  linkedDistributorName: PropTypes.string,
  meLoading: PropTypes.bool.isRequired,
  meError: PropTypes.string,
  intentError: PropTypes.string,
  shippingError: PropTypes.string,
  postCheckoutMessage: PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
  }),
  bankTransferInstructions: PropTypes.string,
  lines: PropTypes.array.isRequired,
  subtotal: PropTypes.number,
  shippingAddressMode: PropTypes.string.isRequired,
  setShippingAddressMode: PropTypes.func.isRequired,
  shippingAddresses: PropTypes.array.isRequired,
  selectedSavedAddress: PropTypes.object,
  setSelectedShippingAddressId: PropTypes.func.isRequired,
  toAddress: PropTypes.object.isRequired,
  setToAddress: PropTypes.func.isRequired,
  useCurrentLocation: PropTypes.func.isRequired,
  locationMessage: PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
  }),
  wantsFacturaToRfc: PropTypes.bool.isRequired,
  setWantsFacturaToRfc: PropTypes.func.isRequired,
  satMissing: PropTypes.arrayOf(PropTypes.string).isRequired,
  canCheckoutMx: PropTypes.bool.isRequired,
  onNavigateProfile: PropTypes.func.isRequired,
  activeShipToSummary: PropTypes.string.isRequired,
  onQuote: PropTypes.func.isRequired,
  canQuote: PropTypes.bool.isRequired,
  shippingLoading: PropTypes.bool.isRequired,
  rates: PropTypes.array.isRequired,
  selectedRateId: PropTypes.any,
  setSelectedRateId: PropTypes.func.isRequired,
  formatRateLabel: PropTypes.func.isRequired,
  isDistributor: PropTypes.bool.isRequired,
  distributorHasCredit: PropTypes.bool.isRequired,
  bankTransferAvailable: PropTypes.bool,
  plaidTransferAvailable: PropTypes.bool,
  checkoutProfile: PropTypes.object.isRequired,
  paymentCollectionMethod: PropTypes.string.isRequired,
  setSelectedPaymentCollectionMethod: PropTypes.func.isRequired,
  stripePublishableKey: PropTypes.string,
  selectedRate: PropTypes.object,
  pricing: PropTypes.object,
  shippingAmount: PropTypes.number,
  processingFeeAmount: PropTypes.number,
  processingFeeConfig: PropTypes.object,
  orderTotal: PropTypes.number,
  clientSecret: PropTypes.string,
  stripePromise: PropTypes.any,
  orderNumber: PropTypes.string,
  StripePayment: PropTypes.elementType.isRequired,
  onStripePaid: PropTypes.func.isRequired,
  plaidSalesOrderDocumentId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  primaryAction: PropTypes.func.isRequired,
  primaryDisabled: PropTypes.bool.isRequired,
  primaryLabel: PropTypes.string.isRequired,
};

MobileCheckoutFlow.defaultProps = {
  currency: "",
  formatMoney: null,
  linkedDistributorName: "",
  meError: null,
  intentError: null,
  shippingError: null,
  postCheckoutMessage: null,
  bankTransferInstructions: null,
  bankTransferAvailable: false,
  plaidTransferAvailable: false,
  subtotal: 0,
  selectedSavedAddress: null,
  locationMessage: null,
  selectedRateId: null,
  stripePublishableKey: "",
  selectedRate: null,
  pricing: null,
  shippingAmount: 0,
  processingFeeAmount: 0,
  processingFeeConfig: null,
  orderTotal: 0,
  clientSecret: null,
  stripePromise: null,
  orderNumber: null,
  plaidSalesOrderDocumentId: null,
};
